import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PageHeader, DataTable, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { useAdoptionStats } from '../hooks/useAdoptionStats';
import type { AdoptionStatRow } from '../types/showcase';

type SortKey = 'downloadCount' | 'takeCount' | 'copyCount' | 'compositeScore';
type SortDir = 'asc' | 'desc';
type PeriodDays = 7 | 30 | 0;

const PERIODS: PeriodDays[] = [7, 30, 0];

function periodLabelKey(days: PeriodDays): string {
  if (days === 7) return 'showcase.adoption.period.d7';
  if (days === 30) return 'showcase.adoption.period.d30';
  return 'showcase.adoption.period.all';
}

/** CSV 셀 이스케이프 — 콤마/따옴표/개행 포함 값을 큰따옴표로 감싸고 내부 따옴표는 2배로 이스케이프. */
function csvCell(value: string | number): string {
  const s = String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildCsv(rows: AdoptionStatRow[]): string {
  const header = ['code', 'nameKo', 'artifact', 'downloadCount', 'takeCount', 'copyCount', 'compositeScore'];
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push(
      [r.code, r.nameKo, r.artifact, r.downloadCount, r.takeCount, r.copyCount, r.compositeScore]
        .map(csvCell)
        .join(',')
    );
  }
  return lines.join('\n');
}

function todayYyyymmdd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

const sortHeaderButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  fontWeight: 700,
  color: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

/**
 * AdoptionStatsPage — 전 모듈 채택 현황 관리 화면 (queue #687 §UAT-2026-07-14-R687 §R687-3).
 * ROLE_ADMIN 전용(라우트 AdminRouteGuard). DataTable(코드/명칭/모듈/다운로드/담기/복사/복합점수) —
 * 복합점수 내림차순 기본 + 컬럼 클릭 정렬(asc/desc 토글) · 0점 행 '미채택' StatusPill 하이라이트 ·
 * CSV 내보내기(클라 생성 + blob 다운로드 재사용 · §R687-3(b)) · 기간 세그먼트(7/30/전체 · 기본 30).
 * 5상태: default(DataTable) · loading(스켈레톤) · empty(EmptyState 방어) · error(ErrorState 재시도) ·
 * dialog(미사용 — CSV 는 즉시 다운로드).
 */
export function AdoptionStatsPage() {
  const { t } = useTranslation();
  const [days, setDays] = useState<PeriodDays>(30);
  const [sortKey, setSortKey] = useState<SortKey>('compositeScore');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data, isLoading, isError, refetch } = useAdoptionStats(days);

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const sortedRows = useMemo(() => {
    const rows = data?.rows ?? [];
    const copy = [...rows];
    copy.sort((a, b) => {
      let cmp = a[sortKey] - b[sortKey];
      if (cmp === 0) cmp = a.downloadCount - b.downloadCount;
      if (cmp === 0) cmp = a.code.localeCompare(b.code);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return copy;
  }, [data, sortKey, sortDir]);

  function sortableHeader(labelKey: string, key: SortKey) {
    const active = sortKey === key;
    const label = t(labelKey);
    return (
      <button
        type="button"
        onClick={() => handleSort(key)}
        style={sortHeaderButtonStyle}
        aria-label={label}
      >
        {label}
        {active && <span aria-hidden="true">{sortDir === 'desc' ? ' ▼' : ' ▲'}</span>}
      </button>
    );
  }

  function handleExportCsv() {
    const rows = sortedRows;
    const csv = buildCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adoption-stats-${days}d-${todayYyyymmdd()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const columns: DataTableColumn<AdoptionStatRow>[] = [
    {
      key: 'code',
      header: t('showcase.adoption.col.code'),
      width: '90px',
      render: (row) => (
        <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)' }}>
          {row.code}
        </code>
      ),
    },
    {
      key: 'name',
      header: t('showcase.adoption.col.name'),
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          {row.nameKo}
          {row.compositeScore === 0 && (
            <StatusPill tone="warning">{t('showcase.adoption.unadopted')}</StatusPill>
          )}
        </span>
      ),
    },
    {
      key: 'module',
      header: t('showcase.adoption.col.module'),
      render: (row) => (
        <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {row.artifact}
        </code>
      ),
    },
    {
      key: 'downloadCount',
      header: sortableHeader('showcase.adoption.col.downloads', 'downloadCount'),
      width: '110px',
      render: (row) => row.downloadCount,
    },
    {
      key: 'takeCount',
      header: sortableHeader('showcase.adoption.col.takes', 'takeCount'),
      width: '90px',
      render: (row) => row.takeCount,
    },
    {
      key: 'copyCount',
      header: sortableHeader('showcase.adoption.col.copies', 'copyCount'),
      width: '90px',
      render: (row) => row.copyCount,
    },
    {
      key: 'compositeScore',
      header: sortableHeader('showcase.adoption.col.score', 'compositeScore'),
      width: '110px',
      render: (row) => (
        <strong style={{ color: 'var(--color-brand-primary)' }}>{row.compositeScore}</strong>
      ),
    },
  ];

  const isEmpty = !isLoading && !isError && (data?.rows.length ?? 0) === 0;
  const showTable = !isLoading && !isError && !isEmpty && !!data;

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: 'var(--spacing-6)' }}>
      <PageHeader
        title={t('showcase.adoption.title')}
        subtitle={t('showcase.adoption.subtitle')}
        breadcrumb={
          <span>
            <Link to="/showcase" style={{ color: 'var(--color-text-secondary)' }}>
              {t('showcase.page.title')}
            </Link>{' '}
            / <span style={{ color: 'var(--color-text-primary)' }}>{t('showcase.adoption.title')}</span>
          </span>
        }
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
            <div
              role="group"
              aria-label={t('showcase.adoption.title')}
              style={{ display: 'flex', gap: 'var(--spacing-1)' }}
            >
              {PERIODS.map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={days === p}
                  onClick={() => setDays(p)}
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-3)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    background: days === p ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                    color: days === p ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                  }}
                >
                  {t(periodLabelKey(p))}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={!showTable}
              data-testid="adoption-export-csv-btn"
              style={{
                padding: 'var(--spacing-1) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-brand-primary)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-brand-primary)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: showTable ? 'pointer' : 'not-allowed',
                opacity: showTable ? 1 : 0.5,
              }}
            >
              {t('showcase.adoption.export')}
            </button>
          </div>
        }
      />

      {isLoading && (
        <div
          role="status"
          aria-busy="true"
          aria-label={t('showcase.feedback.admin.loading')}
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                borderBottom: '1px solid var(--color-surface-border)',
              }}
            >
              <div
                style={{
                  height: 12,
                  width: `${50 + ((i * 13) % 40)}%`,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            </div>
          ))}
          <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
        </div>
      )}

      {isError && (
        <ErrorState
          title={t('showcase.adoption.error.title')}
          message={t('showcase.adoption.error.message')}
          onRetry={() => void refetch()}
        />
      )}

      {isEmpty && <EmptyState title={t('showcase.adoption.empty')} />}

      {showTable && (
        <DataTable
          columns={columns}
          rows={sortedRows}
          rowKey={(r) => r.code}
          emptyMessage={t('showcase.adoption.empty')}
          rowStyle={(row) => (row.compositeScore === 0 ? { background: 'var(--color-surface-muted)' } : undefined)}
        />
      )}
    </div>
  );
}
